import React, { useEffect, useMemo } from "react";
import MaterialReactTable, {
  type MRT_ColumnDef,
  type MRT_RowSelectionState,
} from "material-react-table";
import {
  MoreHorizontal24Filled,
  Edit16Filled,
  Dismiss24Regular,
} from "@fluentui/react-icons";
import {
  Button,
  makeStyles,
  Tooltip,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  Avatar,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Label,
  Dropdown,
  Option,
  useId,
  shorthands,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Switch,
} from "@fluentui/react-components";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getDistributionBasePath } from "./distributionPaths";
import {
  checkUserPermission,
  formatedDate,
  getAssetFundCodeSource,
  getAssetTypeByCaseType,
  getClientType,
  getPaymentStatus,
  getReceiptStatus,
  getYes_NoConversion,
  stripHtmlTags,
} from "../../../utils";
import ReactQuillComponent from "../../../components/ReactQuillComponent";
import { UserPermissionsList } from "../../../config";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import ASCButton from "../../../components/Button";
import MultiLineText from "../../../components/TableMultiLineData/MultiLineData";
import HighlightText from "../../../components/HighlightText/index";
import { Eye16Filled } from "@fluentui/react-icons";
import { ASSET_HAS_STATUS_FIELD } from "./Asset/assetSubTypeConfig";
import {
  AmountOrPercentCell,
  withCurrencyColumns,
} from "../../../components/Common/currencyColumn";

const useStyles = makeStyles({
  wrapper: {
    columnGap: "15px",
    display: "flex",
    cursor: "pointer",
  },
  contentHeader: {
    marginTop: "0",
  },
  scopeArea: {
    display: "grid",
    gridTemplateRows: "repeat(1fr)",
    justifyItems: "start",
    ...shorthands.gap("2px"),
    maxWidth: "400px",
  },
});
const ownerColumn = {
  accessorKey: "joint_indicator",
  header: "Owner",
  filterFn: (row: any, columnId: any, filterValue: any) =>
    getClientType(row.getValue(columnId))
      .toLowerCase()
      .includes(String(filterValue).trim().toLowerCase()),
  Cell: ({ row, column }: any) => (
    <HighlightText
      text={getClientType(row.original.joint_indicator)}
      highlight={String(column.getFilterValue() ?? "")}
    />
  ),
};
const ownerName1Column = { accessorKey: "owner_name1", header: "Owner Name 1" };
const ownerName2Column = { accessorKey: "owner_name2", header: "Owner Name 2" };
const borrowerColumn = { accessorKey: "borrower", header: "Borrower" };
const borrowerName1Column = {
  accessorKey: "borrower_name1",
  header: "Borrower Name 1",
};
const borrowerName2Column = {
  accessorKey: "borrower_name2",
  header: "Borrower Name 2",
};
const policyHolderColumn = {
  accessorKey: "policy_holder",
  header: "Policyholder",
  filterFn: (row: any, columnId: any, filterValue: any) =>
    getClientType(row.getValue(columnId))
      .toLowerCase()
      .includes(String(filterValue).trim().toLowerCase()),
  Cell: ({ row, column }: any) => (
    <HighlightText
      text={getClientType(row.original.policy_holder)}
      highlight={String(column.getFilterValue() ?? "")}
    />
  ),
};
const assuredName1Column = {
  accessorKey: "assured_name1",
  header: "Assured Name 1",
};
const assuredName2Column = {
  accessorKey: "assured_name2",
  header: "Assured Name 2",
};
const providerColumn = { accessorKey: "provider", header: "Provider" };
const policyNumberColumn = {
  accessorKey: "policy_number",
  header: "Policy Number",
};
const makeDateColumn = (accessorKey: string, header: string) => ({
  accessorKey,
  header,
  filterFn: (row: any, columnId: any, filterValue: any) =>
    formatedDate(row.getValue(columnId))
      .toLowerCase()
      .includes(String(filterValue).trim().toLowerCase()),
  Cell: ({ row, column }: any) => (
    <HighlightText
      text={formatedDate(row.original[accessorKey])}
      highlight={String(column.getFilterValue() ?? "")}
    />
  ),
});
const currentValueColumn = {
  accessorKey: "current_value",
  header: "Current Value",
};
const valueDateColumn = {
  accessorKey: "value_date",
  header: "Value Date",
  filterFn: (row: any, columnId: any, filterValue: any) =>
    formatedDate(row.getValue(columnId))
      .toLowerCase()
      .includes(String(filterValue).trim().toLowerCase()),
  Cell: ({ row, column }: any) => (
    <HighlightText
      text={formatedDate(row.original.value_date)}
      highlight={String(column.getFilterValue() ?? "")}
    />
  ),
};

const tableColumnList = {
  assets: {
    investment: [
      ownerColumn,
      ownerName1Column,
      ownerName2Column,
      { accessorKey: "investment_type", header: "Type" },
      { accessorKey: "investment_subtype", header: "SubType" },
      { accessorKey: "investment_account_number", header: "Account Number" },
      currentValueColumn,
      valueDateColumn,
      {
        accessorKey: "investment_start_date",
        header: "Start Date",
        filterFn: (row: any, columnId: any, filterValue: any) =>
          formatedDate(row.getValue(columnId))
            .toLowerCase()
            .includes(String(filterValue).trim().toLowerCase()),
        Cell: ({ row, column }: any) => (
          <HighlightText
            text={formatedDate(row.original.investment_start_date)}
            highlight={String(column.getFilterValue() ?? "")}
          />
        ),
      },
      {
        accessorKey: "investment_end_date",
        header: "End Date",
        filterFn: (row: any, columnId: any, filterValue: any) =>
          formatedDate(row.getValue(columnId))
            .toLowerCase()
            .includes(String(filterValue).trim().toLowerCase()),
        Cell: ({ row, column }: any) => (
          <HighlightText
            text={formatedDate(row.original.investment_end_date)}
            highlight={String(column.getFilterValue() ?? "")}
          />
        ),
      },
      { accessorKey: "investment_status", header: "Status" },
      {
        accessorKey: "status_date",
        header: "Status Date",
        filterFn: (row: any, columnId: any, filterValue: any) =>
          formatedDate(row.getValue(columnId))
            .toLowerCase()
            .includes(String(filterValue).trim().toLowerCase()),
        Cell: ({ row, column }: any) => (
          <HighlightText
            text={formatedDate(row.original.status_date)}
            highlight={String(column.getFilterValue() ?? "")}
          />
        ),
      },
    ],
    shares: [
      ownerColumn,
      ownerName1Column,
      ownerName2Column,
      { accessorKey: "share_type", header: "Type" },
      { accessorKey: "share_subtype", header: "SubType" },
      { accessorKey: "share_company", header: "Company" },
      { accessorKey: "share_current_number", header: "Quantity" },
      { accessorKey: "share_current_price", header: "Current Price" },
      currentValueColumn,
      valueDateColumn,
      {
        accessorKey: "share_date_acquired",
        header: "Date Acquired",
        filterFn: (row: any, columnId: any, filterValue: any) =>
          formatedDate(row.getValue(columnId))
            .toLowerCase()
            .includes(String(filterValue).trim().toLowerCase()),
        Cell: ({ row, column }: any) => (
          <HighlightText
            text={formatedDate(row.original.share_date_acquired)}
            highlight={String(column.getFilterValue() ?? "")}
          />
        ),
      },
      { accessorKey: "share_original_value", header: "Original Value" },
      { accessorKey: "share_status", header: "Status" },
      {
        accessorKey: "status_date",
        header: "Status Date",
        filterFn: (row: any, columnId: any, filterValue: any) =>
          formatedDate(row.getValue(columnId))
            .toLowerCase()
            .includes(String(filterValue).trim().toLowerCase()),
        Cell: ({ row, column }: any) => (
          <HighlightText
            text={formatedDate(row.original.status_date)}
            highlight={String(column.getFilterValue() ?? "")}
          />
        ),
      },
    ],
    "home-personal": [
      ownerColumn,
      ownerName1Column,
      ownerName2Column,
      {
        accessorKey: "home_date_acquired",
        header: "Date Acquired",
        filterFn: (row: any, columnId: any, filterValue: any) =>
          formatedDate(row.getValue(columnId))
            .toLowerCase()
            .includes(String(filterValue).trim().toLowerCase()),
        Cell: ({ row, column }: any) => (
          <HighlightText
            text={formatedDate(row.original.home_date_acquired)}
            highlight={String(column.getFilterValue() ?? "")}
          />
        ),
      },
      { accessorKey: "home_original_value", header: "Original Value" },
      currentValueColumn,
      valueDateColumn,
    ],
    "bank-societies": [
      ownerColumn,
      ownerName1Column,
      ownerName2Column,
      { accessorKey: "bank_account_type", header: "Account Type" },
      { accessorKey: "bank_name", header: "Bank Name" },
      { accessorKey: "bank_account_name", header: "Account Name" },
      { accessorKey: "bank_account_number", header: "Account Number" },
      { accessorKey: "purpose", header: "Purpose" },
      { ...currentValueColumn, header: "Balance" },
      { ...valueDateColumn, header: "Balance Date" },
      { accessorKey: "bank_interest_rate", header: "Interest Rate" },
      {
        accessorKey: "bank_date_opened",
        header: "Date Opened",
        filterFn: (row: any, columnId: any, filterValue: any) =>
          formatedDate(row.getValue(columnId))
            .toLowerCase()
            .includes(String(filterValue).trim().toLowerCase()),
        Cell: ({ row, column }: any) => (
          <HighlightText
            text={formatedDate(row.original.bank_date_opened)}
            highlight={String(column.getFilterValue() ?? "")}
          />
        ),
      },
      { accessorKey: "bank_status", header: "Status" },
      {
        accessorKey: "status_date",
        header: "Status Date",
        filterFn: (row: any, columnId: any, filterValue: any) =>
          formatedDate(row.getValue(columnId))
            .toLowerCase()
            .includes(String(filterValue).trim().toLowerCase()),
        Cell: ({ row, column }: any) => (
          <HighlightText
            text={formatedDate(row.original.status_date)}
            highlight={String(column.getFilterValue() ?? "")}
          />
        ),
      },
    ],
  },
  funds: [
    {
      accessorKey: "fund_code",
      header: "Fund Code",
    },
    {
      accessorKey: "fund_code_source",
      header: "Fund Source Code",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getAssetFundCodeSource(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={getAssetFundCodeSource(row.original.fund_code_source)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "fund_name",
      header: "Fund Name",
    },
    {
      accessorKey: "fund_units",
      header: "Units",
    },
    {
      accessorKey: "unit_price",
      header: "Price",
    },
    {
      accessorKey: "currency_code",
      header: "Currency",
    },
    {
      accessorKey: "price_date",
      header: "Effective Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.price_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },

    {
      accessorKey: "fund_value",
      header: "Fund Value",
    },
    {
      accessorKey: "sector",
      header: "Sector",
    },
    {
      accessorKey: "total_expense_ratio",
      header: "Total Expence Ratio",
    },
    {
      accessorKey: "risk_rating",
      header: "Risk Rating",
    },
  ],
  payments: [
    {
      accessorKey: "payment.start_date",
      header: "Start Date",
      filterFn: (row: any, _columnId: any, filterValue: any) =>
        formatedDate(
          row.original.payment ? row.original.payment.start_date : null,
        )
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(
            row.original.payment ? row.original.payment.start_date : null,
          )}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "payment.end_date",
      header: "End Date",
      filterFn: (row: any, _columnId: any, filterValue: any) =>
        formatedDate(
          row.original.payment ? row.original.payment.end_date : null,
        )
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(
            row.original.payment ? row.original.payment.end_date : null,
          )}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "payment.amount",
      header: "Amount",
    },
    {
      accessorKey: "payment.frequency",
      header: "Frequency",
    },
    {
      accessorKey: "payment.new_business_register",
      header: "Is This New Business",
    },
    {
      accessorKey: "payment.reinvestment",
      header: "Reinvestment",
    },
    {
      accessorKey: "payment.note_text",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.payment?.note_text} />
        </>
      ),
    },
    {
      accessorKey: "payment.payment_type",
      header: "Payment Type",
    },
  ],
  withdrawals: [
    {
      accessorKey: "start_date",
      header: "Start Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.start_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "end_date",
      header: "End Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.end_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "amount",
      header: "Amount",
    },
    {
      accessorKey: "frequency",
      header: "Frequency",
    },
    {
      accessorKey: "high_risk",
      header: "HighRisk",
    },
    {
      accessorKey: "high_risk_note",
      header: "High Risk Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.high_risk_note} />
        </>
      ),
    },
    {
      accessorKey: "reinvestment",
      header: "Reinvestment",
    },
    {
      accessorKey: "note_text",
      header: "Admin Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
  ],
  valuations: [
    {
      accessorKey: "effective_date",
      header: "Effective Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.effective_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "valuation_amount",
      header: "Valuation Amount",
    },

    {
      accessorKey: "note_text",
      header: "Admin Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
  ],
  actions: [
    {
      accessorKey: "business_date",
      header: "Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.business_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },

    {
      accessorKey: "business_action",
      header: "Action",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.business_action} />
        </>
      ),
    },
    {
      accessorKey: "business_assigned",
      header: "Assigned",
    },
    {
      accessorKey: "business_status",
      header: "Status",
    },
  ],
  commission: [
    {
      accessorKey: "status",
      header: "Status",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getReceiptStatus(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={getReceiptStatus(row.original.status)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "commission_type",
      header: "Commission Type",
    },
    {
      accessorKey: "payment_source",
      header: "Source of Payment",
    },
    {
      accessorKey: "frequency",
      header: "Frequency",
    },
    {
      accessorKey: "amount_to_company",
      header: "Amount to Company",
    },
    {
      accessorKey: "start_date",
      header: "Commission Start Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.start_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "end_date",
      header: "End Date",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        formatedDate(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={formatedDate(row.original.end_date)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "payment_count",
      header: "Number of Payments",
    },
    {
      accessorKey: "nb_register",
      header: "Show AMT on NB Register",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getYes_NoConversion(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={getYes_NoConversion(row.original.nb_register)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "note_text",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
  ],
  distribution: [
    {
      accessorKey: "status",
      header: "Status",
      filterFn: (row: any, columnId: any, filterValue: any) =>
        getPaymentStatus(row.getValue(columnId))
          .toLowerCase()
          .includes(String(filterValue).trim().toLowerCase()),
      Cell: ({ row, column }: any) => (
        <HighlightText
          text={getPaymentStatus(row.original.status)}
          highlight={String(column.getFilterValue() ?? "")}
        />
      ),
    },
    {
      accessorKey: "party_type",
      header: "Party Type",
    },
    {
      accessorKey: "party_name",
      header: "Party Name",
    },
    {
      accessorKey: "amount",
      header: "Amount",
      // Split by amount_flag: a distribution row holds either a fixed money
      // amount or a percentage in the same column. Keeps its own Cell, so
      // withCurrencyColumns leaves it alone.
      Cell: ({ row }: any) => (
        <AmountOrPercentCell
          value={row.original.amount}
          amountFlag={row.original.amount_flag}
        />
      ),
    },

    {
      accessorKey: "note_text",
      header: "Note",
      Cell: ({ row }: any) => (
        <>
          <ReactQuillComponent value={row.original.note_text} />
        </>
      ),
    },
  ],
  liabilities: {
    morgages: [
      ownerColumn,
      borrowerColumn,
      borrowerName1Column,
      borrowerName2Column,
      { accessorKey: "provider", header: "Provider" },
      { accessorKey: "account_number", header: "Account Number" },
      { accessorKey: "repayment_method", header: "Repayment Method" },
      { accessorKey: "property_value", header: "Property Value" },
      {
        accessorKey: "loan_valuation_percentage",
        header: "Loan to Valuation %",
      },
      makeDateColumn("mortgage_fixed_rate_end", "End of Fixed Rate"),
      makeDateColumn("start_date", "Start Date"),
      makeDateColumn("end_date", "End Date"),
      { accessorKey: "amount_borrowed", header: "Amount Borrowed" },
      { accessorKey: "amount_outstanding", header: "Amount Outstanding" },
      { accessorKey: "interest_rate", header: "Interest Rate" },
      { accessorKey: "status", header: "Status" },
      makeDateColumn("status_date", "Status Date"),
    ],
    "loans-lease-hp": [
      ownerColumn,
      borrowerColumn,
      borrowerName1Column,
      borrowerName2Column,
      { accessorKey: "provider", header: "Provider" },
      { accessorKey: "purpose", header: "Purpose" },
      { accessorKey: "account_number", header: "Account Number" },
      makeDateColumn("start_date", "Start Date"),
      makeDateColumn("end_date", "End Date"),
      { accessorKey: "amount_borrowed", header: "Amount Borrowed" },
      { accessorKey: "amount_outstanding", header: "Amount Outstanding" },
      { accessorKey: "interest_rate", header: "Interest Rate" },
      { accessorKey: "status", header: "Status" },
      makeDateColumn("status_date", "Status Date"),
    ],
    "credit-cards": [
      ownerColumn,
      borrowerColumn,
      borrowerName1Column,
      borrowerName2Column,
      { accessorKey: "provider", header: "Provider" },
      { accessorKey: "account_number", header: "Account Number" },
      { accessorKey: "amount_outstanding", header: "Amount Outstanding" },
      { accessorKey: "interest_rate", header: "Interest Rate" },
      { accessorKey: "status", header: "Status" },
      makeDateColumn("status_date", "Status Date"),
    ],
  },
  policies: {
    "life-assurance": [
      policyHolderColumn,
      assuredName1Column,
      assuredName2Column,
      providerColumn,
      policyNumberColumn,
      { accessorKey: "lump_sum_benefit", header: "Sum Assured" },
      { accessorKey: "periodic_benefit", header: "Periodic Benefit" },
      { accessorKey: "benefit_frequency", header: "Benefit Frequency" },
      makeDateColumn("start_date", "Start Date"),
      makeDateColumn("end_date", "End Date"),
      { accessorKey: "status", header: "Status" },
      makeDateColumn("status_date", "Status Date"),
    ],
    pensions: [
      policyHolderColumn,
      assuredName1Column,
      assuredName2Column,
      providerColumn,
      { accessorKey: "scheme_name", header: "Scheme Name" },
      policyNumberColumn,
      { accessorKey: "future_value", header: "Future Value" },
      { accessorKey: "total_fund_value", header: "Total Fund Value" },
      { accessorKey: "transfer_value", header: "Transfer Value" },
      makeDateColumn("valuation_date", "Valuation Date"),
      { accessorKey: "contracted_out", header: "Contracted Out" },
      { accessorKey: "lump_sum_benefit", header: "Lump Sum Benefit" },
      { accessorKey: "status", header: "Status" },
      makeDateColumn("status_date", "Status Date"),
    ],
    investments: [
      policyHolderColumn,
      assuredName1Column,
      assuredName2Column,
      providerColumn,
      { accessorKey: "other_reference", header: "Portfolio Ref" },
      policyNumberColumn,
      { accessorKey: "future_value", header: "Future Value" },
      { accessorKey: "total_fund_value", header: "Total Fund Value" },
      makeDateColumn("valuation_date", "Valuation Date"),
      { accessorKey: "lump_sum_benefit", header: "Lump Sum Benefit" },
      { accessorKey: "status", header: "Status" },
      makeDateColumn("status_date", "Status Date"),
    ],
    "savings-plans": [
      policyHolderColumn,
      assuredName1Column,
      assuredName2Column,
      providerColumn,
      policyNumberColumn,
      { accessorKey: "future_value", header: "Future Value" },
      { accessorKey: "total_fund_value", header: "Total Fund Value" },
      makeDateColumn("valuation_date", "Valuation Date"),
      { accessorKey: "lump_sum_benefit", header: "Lump Sum Benefit" },
      { accessorKey: "status", header: "Status" },
      makeDateColumn("status_date", "Status Date"),
    ],
    "income-protection": [
      policyHolderColumn,
      assuredName1Column,
      assuredName2Column,
      providerColumn,
      policyNumberColumn,
      { accessorKey: "periodic_benefit", header: "Periodic Benefit" },
      { accessorKey: "benefit_frequency", header: "Benefit Frequency" },
      { accessorKey: "deffered_period", header: "Deferred Period" },
      { accessorKey: "waiver", header: "Waiver" },
      makeDateColumn("start_date", "Start Date"),
      makeDateColumn("end_date", "End Date"),
      { accessorKey: "lump_sum_benefit", header: "Lump Sum Benefit" },
      { accessorKey: "status", header: "Status" },
      makeDateColumn("status_date", "Status Date"),
    ],
    "health-assurance": [
      policyHolderColumn,
      assuredName1Column,
      assuredName2Column,
      providerColumn,
      policyNumberColumn,
      { accessorKey: "rated", header: "Rate" },
      makeDateColumn("start_date", "Start Date"),
      makeDateColumn("end_date", "End Date"),
      { accessorKey: "lump_sum_benefit", header: "Lump Sum Benefit" },
      { accessorKey: "status", header: "Status" },
      makeDateColumn("status_date", "Status Date"),
    ],
    general: [
      policyHolderColumn,
      assuredName1Column,
      assuredName2Column,
      providerColumn,
      { accessorKey: "purpose", header: "Purpose" },
      policyNumberColumn,
      makeDateColumn("start_date", "Start Date"),
      makeDateColumn("end_date", "End Date"),
      { accessorKey: "lump_sum_benefit", header: "Lump Sum Benefit" },
      { accessorKey: "status", header: "Status" },
      makeDateColumn("status_date", "Status Date"),
    ],
  },
};
const CaseList = (props: any) => {
  const styles = useStyles();
  const {
    clientId,
    case_type,
    caseId,
    subItemId,
    subSubItemId,
    distributionId,
    action,
  } = useParams();
  const loggedUser = useSelector((state: RootState) => state.authUser);
  const navigate = useNavigate();
  // Only the Asset and Liability modules' routes carry a :case_type segment
  const caseTypeSegment =
    ["assets", "liabilities", "policies"].includes(props.section) && case_type
      ? `/${case_type}`
      : "";
  // The Current/Non-current toggle only means something for sub types that
  // actually have a status column. Home & Personal assets have none, so every
  // record classifies as Current and the switch would be an inert control --
  // hide it there rather than let a user flip it to an unchanging list.
  const showStatusToggle =
    ["assets", "liabilities", "policies"].includes(props.module) &&
    (props.module !== "assets" ||
      ASSET_HAS_STATUS_FIELD[case_type ?? ""] !== false);

  const [rowSelection, setRowSelection] = React.useState<MRT_RowSelectionState>(
    {},
  );
  const customColumns = useMemo<MRT_ColumnDef<{ [key: string]: any }>[]>(() => {
    if (props.module === "assets") {
      return withCurrencyColumns(
        (tableColumnList.assets as { [key: string]: any })[
          case_type || "investment"
        ] || [],
      );
    }
    if (props.module === "liabilities") {
      return withCurrencyColumns(
        (tableColumnList.liabilities as { [key: string]: any })[
          case_type || "morgages"
        ] || [],
      );
    }
    if (props.module === "policies") {
      return withCurrencyColumns(
        (tableColumnList.policies as { [key: string]: any })[
          case_type || "life-assurance"
        ] || [],
      );
    }
    if (props.module !== "") {
      return withCurrencyColumns(
        (tableColumnList as { [key: string]: any })[props.module] ?? [],
      );
    }
    return [];
  }, [props.module, case_type]);

  useEffect(() => {
    if (
      Object.keys(rowSelection)[0] !== undefined &&
      props.data &&
      props.data.length > 0
    ) {
      const selectedIndex = Object.keys(rowSelection)[0];
      const selectedRow =
        selectedIndex !== undefined ? props.data[Number(selectedIndex)] : null;

      if (selectedRow && Object.keys(selectedRow).length !== 0) {
        if (props.module == "assets") {
          navigate(
            `/clients/${clientId}/${props.section}${caseTypeSegment}/${selectedRow.asset_id}/${
              props.tab || "asset"
            }/view`,
          );
        } else if (props.module == "actions") {
          if (action == "add") {
            navigate(
              `/clients/${clientId}/${props.section}${caseTypeSegment}/${caseId}/${props.module}/action/${action}`,
            );
          } else {
            navigate(
              `/clients/${clientId}/${props.section}${caseTypeSegment}/${caseId}/${
                props.module
              }/${selectedRow.business_id}/action/${action || "view"}`,
            );
          }
        } else if (props.module == "valuations") {
          if (action == "add") {
            navigate(
              `/clients/${clientId}/${props.section}${caseTypeSegment}/${caseId}/${props.module}/valuation/${action}`,
            );
          } else {
            navigate(
              `/clients/${clientId}/${props.section}${caseTypeSegment}/${caseId}/${
                props.module
              }/${selectedRow.valuation_id}/valuation/${action || "view"}`,
            );
          }
        } else if (props.module == "withdrawals") {
          if (action == "add") {
            navigate(
              `/clients/${clientId}/${props.section}${caseTypeSegment}/${caseId}/${props.module}/withdrawal/${action}`,
            );
          } else {
            navigate(
              `/clients/${clientId}/${props.section}${caseTypeSegment}/${caseId}/${
                props.module
              }/${selectedRow.withdrawal_id}/withdrawal/${action || "view"}`,
            );
          }
        } else if (props.module == "payments") {
          if (action == "add") {
            navigate(
              `/clients/${clientId}/${props.section}${caseTypeSegment}/${caseId}/${
                props.module
              }/${props.activeTab ? props.activeTab : "payment"}/${action}`,
            );
          } else {
            navigate(
              `/clients/${clientId}/${props.section}${caseTypeSegment}/${caseId}/${
                props.module
              }/${selectedRow.payment?.payment_id}/${
                props.activeTab ? props.activeTab : "payment"
              }/view`,
            );
          }
        } else if (props.module == "funds") {
          if (action == "add") {
            navigate(
              `/clients/${clientId}/${props.section}${caseTypeSegment}/${caseId}/${props.module}/fund/${action}`,
            );
          } else {
            navigate(
              `/clients/${clientId}/${props.section}${caseTypeSegment}/${caseId}/${
                props.module
              }/${selectedRow.fund_id}/fund/${action || "view"}`,
            );
          }
        } else if (props.module == "commission" && subItemId) {
          if (action == "add") {
            navigate(
              `/clients/${clientId}/${props.section}${caseTypeSegment}/${caseId}/payments/${subItemId}/commission/${action}`,
            );
          } else {
            navigate(
              `/clients/${clientId}/${props.section}${
                caseTypeSegment
              }/${caseId}/payments/${subItemId}/${props.module}/${
                selectedRow.commission_id
              }/commission/${action || "view"}`,
            );
          }
        } else if (props.module == "distribution") {
          const basePath = getDistributionBasePath({
            clientId,
            section: props.section,
            caseTypeSegment,
            caseId,
            subItemId,
            subSubItemId,
          });
          if (action == "add") {
            navigate(`${basePath}/${action}`);
          } else {
            navigate(
              `${basePath}/${selectedRow.commission_allocation_id}/distribution/${
                action || "view"
              }`,
            );
          }
        } else if (props.module == "liabilities") {
          navigate(
            `/clients/${clientId}/${props.section}${caseTypeSegment}/${
              selectedRow.liability_id
            }/${props.tab || "liability"}/view`,
          );
        } else {
          navigate(
            `/clients/${clientId}/${props.section}${caseTypeSegment}/${selectedRow.policy_id}/${
              props.tab || "policy"
            }/view`,
          );
        }
      }

      // props.handleRowSelection(selectedRow.asset_id, props.mode);
    }
  }, [rowSelection]);

  useEffect(() => {
    if (props.data && props.data.length > 0) {
      let index = 0;
      if (
        [
          "actions",
          "valuations",
          "withdrawals",
          "payments",
          "funds",
          "commission",
          "distribution",
        ].includes(props.module)
      ) {
        // Fees/Charges distribution routes carry no subItemId (no payment).
        if (props.module == "distribution" && subSubItemId && distributionId) {
          index = props.data.findIndex(
            (x: any) => x.commission_allocation_id === distributionId,
          );
        } else if (subItemId) {
          if (props.module == "actions") {
            index = props.data.findIndex(
              (x: any) => x.business_id === subItemId,
            );
          } else if (props.module == "valuations") {
            index = props.data.findIndex(
              (x: any) => x.valuation_id === subItemId,
            );
          } else if (props.module == "withdrawals") {
            index = props.data.findIndex(
              (x: any) => x.withdrawal_id === subItemId,
            );
          } else if (props.module == "payments") {
            index = props.data.findIndex(
              (x: any) => x.payment?.payment_id === subItemId,
            );
          } else if (props.module == "funds") {
            index = props.data.findIndex((x: any) => x.fund_id === subItemId);
          } else if (props.module == "commission") {
            index = props.data.findIndex(
              (x: any) => x.commission_id === subSubItemId,
            );
          }
        }
      } else {
        if (props.module == "assets") {
          index = props.data.findIndex((x: any) => x.asset_id === caseId);
          // props.setCurrentRecord(index + 1);
        } else if (props.module == "liabilities") {
          index = props.data.findIndex((x: any) => x.liability_id === caseId);
        } else {
          index = props.data.findIndex((x: any) => x.policy_id === caseId);
        }
      }

      // findIndex returns -1 when there's no matching record.
      // If neither caseId nor subItemId is set (add page), don't auto-select —
      // doing so would clamp to row 0 and trigger a redirect to view.
      if (index === -1 && !caseId && !subItemId) return;
      // Otherwise clamp so the breadcrumb never shows "Record 0" during a
      // mid-sub-type switch where caseId is defined but data hasn't refreshed yet.
      index = Math.max(index, 0);
      index = Math.max(index, 0);
      setRowSelection({ [index]: true });
      props.setCurrentRecord(index + 1);
    }
  }, [
    props.data,
    props.module,
    subItemId,
    caseId,
    subSubItemId,
    distributionId,
  ]);
  return (
    <div>
      {customColumns && customColumns.length > 0 && (
        <MaterialReactTable
          columns={customColumns}
          data={props.data}
          enableRowSelection
          enableColumnActions={false}
          onRowSelectionChange={setRowSelection}
          enablePagination={false}
          enableRowNumbers
          enableBottomToolbar={false}
          rowNumberMode="static"
          enableMultiRowSelection={false}
          muiTableBodyRowProps={({ row }) => ({
            onClick: row.getToggleSelectedHandler(),
            sx: { cursor: "pointer" },
          })}
          getRowId={(row) => row.id}
          enableRowActions={false}
          renderTopToolbarCustomActions={() =>
            showStatusToggle ? (
              <div style={{ display: "flex", alignItems: "center" }}>
                <Label>Current Cases</Label>
                <Switch
                  checked={props.showNonCurrent}
                  onChange={props.onToggleNonCurrent}
                  label="Non-current Cases"
                  disabled={action === "add" || action === "edit"}
                />
              </div>
            ) : null
          }
          displayColumnDefOptions={{
            "mrt-row-actions": {
              header: "Actions", //change header text
              // size: 300, //make actions column wider
              muiTableHeadCellProps: {
                align: "center",
              },
              muiTableBodyCellProps: {
                align: "center",
              },
            },
          }}
          renderRowActions={({ row, table }) => (
            <div className={styles.wrapper}>
              {checkUserPermission(
                loggedUser,
                UserPermissionsList.ALLOW_VIEW_CLIENT,
              ) && (
                <Tooltip
                  content="View"
                  relationship="label"
                  positioning="below"
                >
                  <ASCButton
                    shape="rounded"
                    appearance="subtle"
                    icon={
                      <Eye16Filled
                        primaryFill="var(--button-color)"
                        onClick={() => {
                          props.handleRowAction("view", row);
                        }}
                      />
                    }
                  />
                </Tooltip>
              )}
              <Tooltip content="Edit" relationship="label" positioning="below">
                <ASCButton
                  shape="rounded"
                  appearance="subtle"
                  icon={
                    <Edit16Filled
                      primaryFill="var(--button-color)"
                      onClick={() => {
                        props.handleRowAction("edit", row);
                      }}
                    />
                  }
                />
              </Tooltip>
            </div>
          )}
          initialState={{
            columnPinning: { right: ["mrt-row-actions"] },
            density: "compact",
          }}
          positionActionsColumn="last"
          positionToolbarAlertBanner="bottom"
          state={{ rowSelection }}
          muiTableContainerProps={
            props.emptyMessage
              ? { sx: { minHeight: "calc(100vh - 385px)" } }
              : undefined
          }
          renderEmptyRowsFallback={
            props.emptyMessage
              ? () => (
                  <p
                    style={{
                      textAlign: "center",
                      padding: "40px 16px",
                      color: "#f00",
                      margin: "auto",
                      height: "calc(100vh - 400px)",
                      display: "flex",
                      flexDirection: "column",
                      fontWeight: "bold",
                      fontSize: "large",
                    }}
                  >
                    {props.emptyMessage}
                  </p>
                )
              : undefined
          }
        />
      )}
    </div>
  );
};

export default CaseList;
